import {InfiniteData, InfiniteQueryObserverOptions, QueryKey, useInfiniteQuery} from "@tanstack/react-query"

import {AMetaWithPagination} from "../services/types.generated"

export const DEFAULT_PAGE_SIZE = 10

export type TInfinitePaginatedQueryParams = {
  page?: number
  per_page?: number
}

type TQueryResponseWithPagination = {
  data?: {
    meta?: AMetaWithPagination
  }
}

export function useInfinitePaginatedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: (params: TInfinitePaginatedQueryParams) => Promise<TQueryFnData>,
  options?: {
    pageSize?: number
    initialPageParam?: number
  } & Omit<
    InfiniteQueryObserverOptions<TQueryFnData, TError, TData, TQueryKey, number>,
    "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam" | "getPreviousPageParam"
  >
): ReturnType<typeof useInfiniteQuery<TQueryFnData, TError, TData, TQueryKey, number>> {
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE
  const initialPageParam = options?.initialPageParam ?? 1

  /* eslint-disable @tanstack/query/exhaustive-deps */
  return useInfiniteQuery({
    queryKey,
    queryFn: async ({pageParam = initialPageParam}) => queryFn({page: pageParam, per_page: pageSize}),
    initialPageParam,
    getNextPageParam: (lastPage: TQueryFnData) => {
      const response = lastPage as TQueryResponseWithPagination
      const pagination = response?.data?.meta?.pagination
      if (!pagination) {
        return undefined
      }

      const {total_count, current_page, per_page} = pagination
      if (total_count == null || current_page == null || per_page == null) {
        return undefined
      }

      return total_count > current_page * per_page ? current_page + 1 : undefined
    },
    getPreviousPageParam: (firstPage: TQueryFnData) => {
      const response = firstPage as TQueryResponseWithPagination
      const pagination = response?.data?.meta?.pagination
      if (!pagination) {
        return undefined
      }

      const {current_page} = pagination
      if (current_page == null) {
        return undefined
      }

      return current_page === 1 ? undefined : current_page - 1
    },
    ...options,
  })
}
